import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Upload, Camera } from "lucide-react";
import PassportPhotoUploader from "./PassportPhotoUploader";
import Field, { inputCls } from "./employee-dialog/Field";
import RentReceiptUploader from "./employee-dialog/RentReceiptUploader";
import TransferDialog from "./employee-dialog/TransferDialog";
import { BankAccountVerify } from "./BankAccountVerify";
import { useAuth } from "@/context/AuthContext";
import { isRestricted } from "@/lib/roles";
import { hasRole } from "@/lib/roles";
import { MoneyInput } from "@/components/MoneyInput";

const EMPTY = {
    first_name: "",
    last_name: "",
    other_name: "",
    full_name: "",
    employee_no: "",
    email: "",
    work_email: "",
    phone: "",
    position: "",
    department: "",
    company: "",
    division: "",
    joining_date: "",
    salary: 0,
    leave_entitlement: 20,
    address: "",
    city: "",
    state: "",
    country: "",
    nationality: "",
    date_of_birth: "",
    gender: "",
    employment_type: "Full-time",
    status: "active",
    photo: "",
    notes: "",
    category: "",
    group: "",
    cadre: "",
    qualification: "",
    course_of_study: "",
    additional_qualification: "",
    bvn: "",
    nin: "",
    tax_id: "",
    pension_id: "",
    pfa: "",
    bank_name: "",
    bank_account_number: "",
    bank_account_name: "",
    last_increment_date: "",
    resignation_date: "",
    termination_date: "",
    annual_rent: 0,
    other_allowance: 0,
    rent_receipt_url: "",
    rent_receipt_expiry: "",
    rent_receipt_uploaded_at: "",
    // Opening balances (only used on CREATE — auto-creates Loan + Advance records)
    // Advances are always single-installment (deducted next payroll run)
    opening_loan_balance: 0,
    opening_loan_installment: 0,
    opening_advance_balance: 0,
};

const QUALIFICATION_SUGGESTIONS = ["SSCE", "OND", "NCE", "HND", "BSc", "BEng", "BA", "MBA", "MSc", "MEng", "PhD", "Professional Certification"];

// Keep a saved value selectable even when it's no longer on the reference list
const withCurrent = (list, current) => (current && !list.includes(current) ? [current, ...list] : list);

export default function EmployeeDialog({ open, onClose, onSaved, employee, departments }) {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [bankCheck, setBankCheck] = useState(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    const { user } = useAuth();
    const noPay = isRestricted(user);
    // Pay is set at creation; afterwards only an Admin may change it
    const payLocked = !!employee?.id && !hasRole(user, "admin");
    const [error, setError] = useState("");
    const [previewNo, setPreviewNo] = useState("");        // live preview of next employee_no
    const [previewError, setPreviewError] = useState(""); // e.g. "Company Code not set"
    const [transferOpen, setTransferOpen] = useState(false);
    const [transferData, setTransferData] = useState({ from_company_legal_name: "", new_company_legal_name: "", effective_date: "" });
    const [transferring, setTransferring] = useState(false);
    // Autocomplete suggestions (free-text fields)
    const [divisionSuggestions, setDivisionSuggestions] = useState([]);
    const [countryList, setCountryList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [pfaList, setPfaList] = useState([]);
    const [bankList, setBankList] = useState([]);
    const fileRef = useRef(null);

    useEffect(() => {
        if (open) {
            setError("");
            setForm(employee ? { ...EMPTY, ...employee } : EMPTY);
            setPreviewNo("");
            setPreviewError("");
        }
    }, [open, employee]);

    // Load distinct Company/Division values once the dialog opens — used for datalist autocomplete
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            // Load each list independently so one failing request never empties the others
            const [d, cn, st, pf, bk] = await Promise.allSettled([
                api.get("/reference/divisions"),
                api.get("/reference/countries"),
                api.get("/reference/states"),
                api.get("/reference/pfas"),
                api.get("/reference/banks"),
            ]);
            if (cancelled) return;
            const val = (r, key) => (r.status === "fulfilled" ? r.value.data?.[key] || [] : []);
            setDivisionSuggestions(val(d, "divisions"));
            setCountryList(val(cn, "countries"));
            setStateList(val(st, "states"));
            setPfaList(val(pf, "pfas"));
            setBankList(val(bk, "banks"));
        })();
        return () => { cancelled = true; };
    }, [open]);

    // Live preview of auto-generated Employee No — fires whenever Joining Date changes
    // (only for NEW employees; editing existing employees keeps the saved number).
    useEffect(() => {
        if (!open || employee) return;
        let cancelled = false;
        const fetchPreview = async () => {
            try {
                const { data } = await api.get("/employees/next-employee-no", {
                    params: form.joining_date ? { joining_date: form.joining_date } : {},
                });
                if (cancelled) return;
                if (data?.ok) {
                    setPreviewNo(data.employee_no || "");
                    setPreviewError("");
                } else {
                    setPreviewNo("");
                    setPreviewError(data?.error || "");
                }
            } catch (err) {
                if (!cancelled) {
                    setPreviewNo("");
                    setPreviewError(formatApiError(err.response?.data?.detail) || "");
                }
            }
        };
        fetchPreview();
        return () => { cancelled = true; };
    }, [open, employee, form.joining_date]);

    if (!open) return null;

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onPhotoChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Photo must be under 2MB");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => set("photo", reader.result);
        reader.readAsDataURL(file);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (bankCheck && !bankCheck.match && bankCheck.for === `${form.bank_name}|${form.bank_account_number}`) {
            toast.warning(`Saved with a bank name mismatch — the bank says "${bankCheck.account_name}"`);
        }
        // If editing an existing employee whose Company has been changed to a non-empty
        // different value, intercept the save and open the Transfer Letter dialog instead.
        const isCompanyChange = !!employee?.id
            && (employee.company || "").trim() !== ""
            && (form.company || "").trim() !== ""
            && (employee.company || "").trim().toLowerCase() !== (form.company || "").trim().toLowerCase();
        if (isCompanyChange) {
            setTransferData({
                from_company_legal_name: "",
                new_company_legal_name: "",
                effective_date: new Date().toISOString().slice(0, 10),
            });
            setTransferOpen(true);
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form, salary: Number(form.salary) || 0, other_allowance: Math.max(0, Number(form.other_allowance) || 0) };
            if (employee?.id) {
                // On UPDATE, opening balances are irrelevant — never resend them
                delete payload.opening_loan_balance;
                delete payload.opening_loan_installment;
                delete payload.opening_advance_balance;
                const res = await api.put(`/employees/${employee.id}`, payload);
                toast.success(res.status === 202 ? "Changes sent to Admin/HR for approval" : "Employee updated");
            } else {
                // On CREATE, coerce opening balance fields to numbers
                payload.opening_loan_balance = Number(payload.opening_loan_balance) || 0;
                payload.opening_loan_installment = Number(payload.opening_loan_installment) || 0;
                payload.opening_advance_balance = Number(payload.opening_advance_balance) || 0;
                const res = await api.post("/employees", payload);
                toast.success(res.status === 202 ? "New employee sent to Admin/HR for approval" : "Employee added" + (payload.email ? " · onboarding email sent" : ""));
            }
            onSaved?.();
            onClose();
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const confirmTransfer = async () => {
        if (!transferData.new_company_legal_name.trim()) {
            toast.error("New company legal name is required");
            return;
        }
        setTransferring(true);
        try {
            // 1) Persist non-company changes first (the transfer endpoint will also update company)
            const payload = { ...form, salary: Number(form.salary) || 0, other_allowance: Math.max(0, Number(form.other_allowance) || 0) };
            // Strip company — the transfer endpoint owns that mutation
            const { company: _newCompanyIgnored, ...rest } = payload;
            void _newCompanyIgnored;
            await api.put(`/employees/${employee.id}`, rest);

            // 2) Trigger transfer — backend updates company, saves history, generates PDF, auto-uploads doc
            const res = await api.post(
                `/employees/${employee.id}/transfer-letter`,
                {
                    new_company: form.company,
                    new_company_legal_name: transferData.new_company_legal_name.trim(),
                    from_company_legal_name: transferData.from_company_legal_name.trim(),
                    effective_date: transferData.effective_date || new Date().toISOString().slice(0, 10),
                    previous_company: employee.company || "",
                },
                { responseType: "blob" },
            );

            // 3) Download the PDF
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const safeName = ((form.full_name || `${form.first_name}_${form.last_name}`).trim() || "employee")
                .replace(/\s+/g, "_");
            a.download = `transfer_letter_${safeName}_${transferData.effective_date || "today"}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);

            toast.success("Employee transferred. Letter downloaded and saved to documents.");
            setTransferOpen(false);
            onSaved?.();
            onClose();
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            toast.error(msg);
        } finally {
            setTransferring(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
            data-testid="employee-dialog"
            onClick={onClose}
        >
            <div
                className="bg-white border border-ink/10 w-full max-w-3xl animate-fade-in-up"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-8 py-6 border-b border-ink/10">
                    <div>
                        <div className="overline">{employee ? "Edit record" : "New record"}</div>
                        <h3 className="font-display font-bold text-2xl tracking-tight mt-1">
                            {employee ? "Edit employee" : "Add employee"}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-ink/5 text-ink/60 hover:text-ink"
                        data-testid="employee-dialog-close"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={onSubmit} className="px-8 py-8 grid grid-cols-1 md:grid-cols-6 gap-6">
                    {/* Photo */}
                    <div className="md:col-span-2 md:row-span-2">
                        <div className="overline mb-2">Photo</div>
                        <div className="aspect-square bg-ink/5 border border-ink/10 overflow-hidden flex items-center justify-center">
                            {form.photo ? (
                                <img src={form.photo} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <div className="text-ink/30 text-xs uppercase tracking-[0.2em]">No photo</div>
                            )}
                        </div>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={onPhotoChange}
                            data-testid="photo-file-input"
                        />
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-ink/20 py-2.5 text-sm hover:bg-ink hover:text-white transition-colors"
                            data-testid="photo-upload-button"
                        >
                            <Upload className="w-4 h-4" /> Upload photo
                        </button>
                        <button
                            type="button"
                            onClick={() => setCameraOpen(true)}
                            className="mt-2 w-full inline-flex items-center justify-center gap-2 border border-ink/20 py-2.5 text-sm hover:bg-ink hover:text-white transition-colors"
                            data-testid="photo-camera-button"
                        >
                            <Camera className="w-4 h-4" /> Take passport photo
                        </button>
                        <PassportPhotoUploader
                            open={cameraOpen}
                            onClose={() => setCameraOpen(false)}
                            initialMode="camera"
                            employeeId={employee?.id}
                            onPick={employee?.id ? undefined : (dataUrl) => set("photo", dataUrl)}
                            onSaved={() => toast.success("Passport photo saved to this employee")}
                        />
                        {form.photo && (
                            <button
                                type="button"
                                onClick={() => set("photo", "")}
                                className="mt-2 w-full text-xs text-ink/50 hover:text-destructive"
                                data-testid="photo-remove-button"
                            >
                                Remove photo
                            </button>
                        )}
                    </div>

                    <Field label="First name" testid="field-first-name" required span={2}>
                        <input
                            value={form.first_name}
                            onChange={(e) => set("first_name", e.target.value)}
                            required
                            className={inputCls}
                            data-testid="input-first-name"
                        />
                    </Field>
                    <Field label="Other name" testid="field-other-name" span={2}>
                        <input
                            value={form.other_name}
                            onChange={(e) => set("other_name", e.target.value)}
                            placeholder="Middle name"
                            className={inputCls}
                            data-testid="input-other-name"
                        />
                    </Field>
                    <Field label="Last name" testid="field-last-name" required span={2}>
                        <input
                            value={form.last_name}
                            onChange={(e) => set("last_name", e.target.value)}
                            required
                            className={inputCls}
                            data-testid="input-last-name"
                        />
                    </Field>

                    <Field
                        label="Full name (legal)"
                        testid="field-full-name"
                        span={6}
                        hint="Full name exactly as it appears on legal documents — BVN, bank account, NIN. Used on all official letters & payslips. Auto-fills as 'Lastname Firstname Othername' when blank."
                    >
                        <input
                            value={form.full_name}
                            onChange={(e) => set("full_name", e.target.value)}
                            placeholder={[form.last_name, form.first_name, form.other_name].filter(Boolean).join(" ") || "e.g. Adebayo Adekunle Oluwaseun"}
                            className={inputCls}
                            data-testid="input-full-name"
                        />
                    </Field>

                    <Field label="Email (personal)" testid="field-email" span={3}>
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => set("email", e.target.value)}
                            placeholder="Optional"
                            className={inputCls}
                            data-testid="input-email"
                        />
                    </Field>
                    <Field label="Work email" testid="field-work-email" span={3}>
                        <input
                            type="email"
                            value={form.work_email}
                            onChange={(e) => set("work_email", e.target.value)}
                            placeholder="e.g. firstname.lastname@company.com"
                            className={inputCls}
                            data-testid="input-work-email"
                        />
                    </Field>

                    <Field
                        label="Employee No"
                        testid="field-employee-no"
                        span={3}
                        hint={employee
                            ? undefined
                            : previewError
                                ? previewError
                                : previewNo
                                    ? <>Will be auto-generated as <span className="font-mono font-semibold text-klein">{previewNo}</span> on save (leave blank to use). Manual entry overrides.</>
                                    : "Leave blank to auto-generate from your Company Code + month/year of joining + serial."
                        }
                    >
                        <input
                            value={form.employee_no}
                            onChange={(e) => set("employee_no", e.target.value)}
                            placeholder={!employee && previewNo ? previewNo : "Auto-generated if blank"}
                            className={inputCls + (!employee && previewNo && !form.employee_no ? " placeholder:text-klein/60 placeholder:font-mono" : "")}
                            data-testid="input-employee-no"
                        />
                    </Field>
                    <Field label="Phone" testid="field-phone" span={3}>
                        <input
                            value={form.phone}
                            onChange={(e) => set("phone", e.target.value)}
                            className={inputCls}
                            data-testid="input-phone"
                        />
                    </Field>
                    <Field label="Joining date" testid="field-joining" span={3}>
                        <input
                            type="date"
                            value={form.joining_date}
                            onChange={(e) => set("joining_date", e.target.value)}
                            className={inputCls}
                            data-testid="input-joining-date"
                        />
                    </Field>

                    <Field label="Position" testid="field-position" span={3}>
                        <input
                            value={form.position}
                            onChange={(e) => set("position", e.target.value)}
                            className={inputCls}
                            data-testid="input-position"
                        />
                    </Field>
                    <Field label="Department" testid="field-department" span={3}>
                        <select
                            value={form.department}
                            onChange={(e) => set("department", e.target.value)}
                            className={inputCls + " bg-transparent"}
                            data-testid="input-department"
                        >
                            <option value="">Select department</option>
                            {departments.map((d) => (
                                <option key={d.id} value={d.name}>{d.name}</option>
                            ))}
                        </select>
                    </Field>

                    {/* "Company" field intentionally hidden per product decision (Feb 2026).
                        The DB field is preserved on existing records for salary-structure & PAYE
                        report grouping compatibility. New employees will be created with company="". */}
                    <Field label="Division" testid="field-division" span={6}>
                        <select
                            value={form.division}
                            onChange={(e) => set("division", e.target.value)}
                            className={inputCls}
                            data-testid="input-division"
                        >
                            <option value="">{divisionSuggestions.length ? "— None —" : "Set up divisions in Settings"}</option>
                            {(form.division && !divisionSuggestions.includes(form.division) ? [form.division, ...divisionSuggestions] : divisionSuggestions)
                                .map((v) => <option key={v} value={v}>{v}</option>)}
                        </select>
                    </Field>

                    {!noPay && (<>
                    <Field label="Salary (monthly, ₦)" testid="field-salary" span={2}>
                        <MoneyInput
                            value={form.salary}
                            onChange={(e) => set("salary", e.target.value)}
                            className={inputCls + (payLocked ? " opacity-60 cursor-not-allowed" : "")}
                            disabled={payLocked}
                            title={payLocked ? "Only an Admin can change pay after the record is created" : undefined}
                            data-testid="input-salary"
                        />
                    </Field>
                    <Field label="Other allowance (monthly, ₦ · non-taxable)" testid="field-other-allowance" span={2}>
                        <MoneyInput
                            value={form.other_allowance ?? 0}
                            onChange={(e) => set("other_allowance", e.target.value)}
                            className={inputCls + (payLocked ? " opacity-60 cursor-not-allowed" : "")}
                            disabled={payLocked}
                            title={payLocked ? "Only an Admin can change pay after the record is created" : undefined}
                            data-testid="input-other-allowance"
                        />
                    </Field>
                    {payLocked && (
                        <p className="md:col-span-6 -mt-2 text-xs text-ink/50" data-testid="pay-locked-hint">
                            Salary and other allowance are locked after creation — only an Admin can change them (via Salary change on the profile).
                        </p>
                    )}
                    </>)}
                    <Field label="Leave days / year" testid="field-leave-entitlement" span={2}>
                        <input
                            type="number"
                            min="0"
                            max="60"
                            step="1"
                            value={form.leave_entitlement}
                            onChange={(e) => set("leave_entitlement", e.target.value)}
                            className={inputCls}
                            data-testid="input-leave-entitlement"
                        />
                    </Field>
                    <Field label="Employment type" testid="field-employment-type" span={2}>
                        <select
                            value={form.employment_type}
                            onChange={(e) => set("employment_type", e.target.value)}
                            className={inputCls + " bg-transparent"}
                            data-testid="input-employment-type"
                        >
                            <option>Full-time</option>
                            <option>Part-time</option>
                            <option>Contract</option>
                            <option>Intern</option>
                        </select>
                    </Field>
                    <Field label="Status" testid="field-status" span={2}>
                        <select
                            value={form.status}
                            onChange={(e) => set("status", e.target.value)}
                            className={inputCls + " bg-transparent"}
                            data-testid="input-status"
                        >
                            <option value="active">Active</option>
                            <option value="on_leave">On leave</option>
                            <option value="resigned">Resigned</option>
                            <option value="terminated">Terminated</option>
                        </select>
                    </Field>
                    <Field label="Resignation date" testid="field-resignation-date" span={2}>
                        <input
                            type="date"
                            value={form.resignation_date || ""}
                            onChange={(e) => set("resignation_date", e.target.value)}
                            className={inputCls}
                            data-testid="input-resignation-date"
                        />
                    </Field>
                    <Field label="Termination date" testid="field-termination-date" span={2}>
                        <input
                            type="date"
                            value={form.termination_date || ""}
                            onChange={(e) => set("termination_date", e.target.value)}
                            className={inputCls}
                            data-testid="input-termination-date"
                        />
                    </Field>
                    {!noPay && <Field label="Last increment date" testid="field-last-increment" span={2}>
                        <input
                            type="date"
                            value={form.last_increment_date || ""}
                            onChange={(e) => set("last_increment_date", e.target.value)}
                            className={inputCls}
                            data-testid="input-last-increment"
                            readOnly
                        />
                        <p className="text-[11px] text-ink/40 mt-1">Auto-set when you record a salary increment</p>
                    </Field>}

                    {/* Classification & Qualification */}
                    <div className="md:col-span-6 mt-2 pt-6 border-t border-ink/10">
                        <div className="overline">Section</div>
                        <h4 className="mt-1 font-display font-bold text-lg text-ink">Classification & qualification</h4>
                    </div>
                    <Field label="Category" testid="field-category" span={2}>
                        <select
                            value={form.category}
                            onChange={(e) => set("category", e.target.value)}
                            className={inputCls + " bg-transparent"}
                            data-testid="input-category"
                        >
                            <option value="">Select</option>
                            <option value="Payroll">Payroll</option>
                            <option value="Non-Payroll">Non-Payroll</option>
                        </select>
                    </Field>
                    <Field label="Group" testid="field-group" span={2}>
                        <select
                            value={form.group}
                            onChange={(e) => set("group", e.target.value)}
                            className={inputCls + " bg-transparent"}
                            data-testid="input-group"
                        >
                            <option value="">Select</option>
                            <option value="Local">Local</option>
                            <option value="Expatriate">Expatriate</option>
                        </select>
                    </Field>
                    <Field label="Cadre" testid="field-cadre" span={2}>
                        <select
                            value={form.cadre}
                            onChange={(e) => set("cadre", e.target.value)}
                            className={inputCls + " bg-transparent"}
                            data-testid="input-cadre"
                        >
                            <option value="">Select</option>
                            <option value="Management Staff">Management Staff</option>
                            <option value="Senior Staff">Senior Staff</option>
                            <option value="Junior Staff">Junior Staff</option>
                        </select>
                    </Field>
                    <Field label="Highest qualification" testid="field-qualification" span={3}>
                        <input
                            list="employee-qualification-options"
                            value={form.qualification}
                            onChange={(e) => set("qualification", e.target.value)}
                            placeholder="e.g. BSc, MBA, HND"
                            className={inputCls}
                            data-testid="input-qualification"
                        />
                        <datalist id="employee-qualification-options">
                            {QUALIFICATION_SUGGESTIONS.map((q) => <option key={q} value={q} />)}
                        </datalist>
                    </Field>
                    <Field label="Course of study" testid="field-course-of-study" span={3}>
                        <input
                            value={form.course_of_study}
                            onChange={(e) => set("course_of_study", e.target.value)}
                            placeholder="e.g. Accounting, Computer Science"
                            className={inputCls}
                            data-testid="input-course-of-study"
                        />
                    </Field>
                    <Field label="Additional qualification" testid="field-additional-qualification" span={6}>
                        <input
                            value={form.additional_qualification}
                            onChange={(e) => set("additional_qualification", e.target.value)}
                            placeholder="e.g. ACCA, ICAN, PMP"
                            className={inputCls}
                            data-testid="input-additional-qualification"
                        />
                    </Field>

                    <Field label="Date of birth" testid="field-dob" span={3}>
                        <input
                            type="date"
                            value={form.date_of_birth}
                            onChange={(e) => set("date_of_birth", e.target.value)}
                            className={inputCls}
                            data-testid="input-dob"
                        />
                    </Field>
                    <Field label="Gender" testid="field-gender" span={3}>
                        <select
                            value={form.gender}
                            onChange={(e) => set("gender", e.target.value)}
                            className={inputCls + " bg-transparent"}
                            data-testid="input-gender"
                        >
                            <option value="">Prefer not to say</option>
                            <option>Female</option>
                            <option>Male</option>
                            <option>Non-binary</option>
                            <option>Other</option>
                        </select>
                    </Field>

                    <Field label="Address" testid="field-address" span={6}>
                        <input
                            value={form.address}
                            onChange={(e) => set("address", e.target.value)}
                            className={inputCls}
                            data-testid="input-address"
                        />
                    </Field>
                    <Field label="State" testid="field-state" span={3}>
                        <select
                            value={form.state || form.city || ""}
                            onChange={(e) => set("state", e.target.value)}
                            className={inputCls + " bg-transparent"}
                            data-testid="input-state"
                        >
                            <option value="">— Select —</option>
                            {withCurrent(stateList, form.state || form.city).map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Country" testid="field-country" span={3}>
                        <select
                            value={form.country || ""}
                            onChange={(e) => set("country", e.target.value)}
                            className={inputCls + " bg-transparent"}
                            data-testid="input-country"
                        >
                            <option value="">— Select —</option>
                            {withCurrent(countryList, form.country).map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Nationality" testid="field-nationality" span={6}>
                        <select
                            value={form.nationality || ""}
                            onChange={(e) => set("nationality", e.target.value)}
                            className={inputCls + " bg-transparent"}
                            data-testid="input-nationality"
                        >
                            <option value="">— Select —</option>
                            {withCurrent(countryList, form.nationality).map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </Field>

                    <Field label="Notes" testid="field-notes" span={6}>
                        <textarea
                            rows={3}
                            value={form.notes}
                            onChange={(e) => set("notes", e.target.value)}
                            className={inputCls + " resize-none"}
                            data-testid="input-notes"
                        />
                    </Field>

                    {/* Opening balances (create-only). Auto-creates approved Loan / Advance
                        records so they flow through the standard payroll deduction pipeline. */}
                    {!employee?.id && !noPay && (
                        <>
                            <div className="md:col-span-6 mt-2 pt-6 border-t border-ink/10">
                                <div className="overline">Section</div>
                                <h4 className="mt-1 font-display font-bold text-lg text-ink">Opening balances</h4>
                                <p className="text-xs text-ink/50 mt-1">
                                    Carry-over loan / advance balances from your previous system. We&apos;ll
                                    auto-create approved records that get deducted from monthly payroll.
                                    Leave zero if not applicable.
                                </p>
                            </div>
                            <Field label="Opening loan balance (₦)" testid="field-opening-loan" span={3}>
                                <MoneyInput
                                    value={form.opening_loan_balance}
                                    onChange={(e) => set("opening_loan_balance", e.target.value)}
                                    className={inputCls}
                                    data-testid="input-opening-loan"
                                />
                            </Field>
                            <Field label="Loan · number of installments" testid="field-opening-loan-inst" span={3}>
                                <input
                                    type="number" min="1" step="1"
                                    value={form.opening_loan_installment}
                                    onChange={(e) => set("opening_loan_installment", e.target.value)}
                                    className={inputCls}
                                    placeholder="Months (e.g. 6)"
                                    data-testid="input-opening-loan-installment"
                                />
                            </Field>
                            <Field label="Opening salary advance balance (₦)" testid="field-opening-adv" span={6}>
                                <MoneyInput
                                    value={form.opening_advance_balance}
                                    onChange={(e) => set("opening_advance_balance", e.target.value)}
                                    className={inputCls}
                                    placeholder="Deducted in a single next payroll run"
                                    data-testid="input-opening-advance"
                                />
                            </Field>
                        </>
                    )}

                    {/* Compliance & Identity */}
                    <div className="md:col-span-6 mt-2 pt-6 border-t border-ink/10">
                        <div className="overline">Section</div>
                        <h4 className="mt-1 font-display font-bold text-lg text-ink">Compliance & identity</h4>
                    </div>
                    <Field label="BVN" testid="field-bvn" span={3}>
                        <input
                            value={form.bvn}
                            onChange={(e) => set("bvn", e.target.value.replace(/\D/g, "").slice(0, 11))}
                            inputMode="numeric"
                            maxLength={11}
                            placeholder="11-digit Bank Verification Number"
                            className={inputCls}
                            data-testid="input-bvn"
                        />
                    </Field>
                    <Field label="NIN" testid="field-nin" span={3}>
                        <input
                            value={form.nin}
                            onChange={(e) => set("nin", e.target.value.replace(/\D/g, "").slice(0, 11))}
                            inputMode="numeric"
                            maxLength={11}
                            placeholder="11-digit National ID Number"
                            className={inputCls}
                            data-testid="input-nin"
                        />
                    </Field>
                    <Field label="Taxpayer ID (TIN)" testid="field-tax-id" span={3}>
                        <input
                            value={form.tax_id}
                            onChange={(e) => set("tax_id", e.target.value)}
                            className={inputCls}
                            data-testid="input-tax-id"
                        />
                    </Field>
                    <Field label="Pension ID (RSA PIN)" testid="field-pension-id" span={3}>
                        <input
                            value={form.pension_id}
                            onChange={(e) => set("pension_id", e.target.value)}
                            className={inputCls}
                            data-testid="input-pension-id"
                        />
                    </Field>
                    <Field label="Pension Fund Admin (PFA)" testid="field-pfa" span={6}>
                        <select
                            value={form.pfa || ""}
                            onChange={(e) => set("pfa", e.target.value)}
                            className={inputCls + " bg-transparent"}
                            data-testid="input-pfa"
                        >
                            <option value="">— Select PFA —</option>
                            {withCurrent(pfaList, form.pfa).map((p) => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </select>
                    </Field>

                    {/* Bank details */}
                    <div className="md:col-span-6 mt-2 pt-6 border-t border-ink/10">
                        <div className="overline">Section</div>
                        <h4 className="mt-1 font-display font-bold text-lg text-ink">Bank details (for payroll)</h4>
                    </div>
                    <Field label="Bank name" testid="field-bank-name" span={3}>
                        <select
                            value={form.bank_name || ""}
                            onChange={(e) => set("bank_name", e.target.value)}
                            className={inputCls + " bg-transparent"}
                            data-testid="input-bank-name"
                        >
                            <option value="">— Select bank —</option>
                            {withCurrent(bankList, form.bank_name).map((b) => (
                                <option key={b} value={b}>{b}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Account number" testid="field-bank-account-number" span={3}>
                        <input
                            value={form.bank_account_number}
                            onChange={(e) => set("bank_account_number", e.target.value.replace(/\D/g, "").slice(0, 10))}
                            inputMode="numeric"
                            maxLength={10}
                            placeholder="10-digit NUBAN"
                            className={inputCls}
                            data-testid="input-bank-account-number"
                        />
                    </Field>
                    <BankAccountVerify form={form} set={set} result={bankCheck} onResult={setBankCheck} inputCls={inputCls} />

                    {!noPay && (<>
                    {/* Rent (Nigeria Tax Act 2026 — rent relief) */}
                    <div className="md:col-span-6 mt-4 pt-4 border-t border-ink/10">
                        <div className="overline">Tax relief</div>
                        <h4 className="mt-1 font-display font-bold text-lg text-ink">House rent</h4>
                        <p className="text-xs text-ink/50 mt-1">Nigeria Tax Act 2026 grants rent relief equal to the lower of 20% of annual rent or ₦500,000. Upload a receipt to support the claim — renewable on expiry.</p>
                    </div>
                    <Field label="Annual rent (₦)" testid="field-annual-rent" span={3}>
                        <MoneyInput
                            value={form.annual_rent || ""}
                            onChange={(e) => set("annual_rent", Number(e.target.value) || 0)}
                            className={inputCls}
                            placeholder="e.g. 1,200,000"
                            data-testid="input-annual-rent"
                        />
                    </Field>
                    <Field label="Receipt expiry date" testid="field-rent-expiry" span={3}>
                        <input
                            type="date"
                            value={form.rent_receipt_expiry || ""}
                            onChange={(e) => set("rent_receipt_expiry", e.target.value)}
                            className={inputCls}
                            data-testid="input-rent-expiry"
                        />
                    </Field>
                    <Field label="Rent receipt (PDF/image, max 1MB)" testid="field-rent-receipt" span={6}>
                        <RentReceiptUploader form={form} setForm={setForm} />
                    </Field>
                    </>)}

                    {error && (
                        <div className="md:col-span-6 border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                            {error}
                        </div>
                    )}

                    <div className="md:col-span-6 flex items-center justify-end gap-3 pt-2 border-t border-ink/10">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-3 border border-ink/20 text-sm hover:bg-ink/5"
                            data-testid="employee-dialog-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-6 py-3 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60"
                            data-testid="employee-dialog-save"
                        >
                            {saving ? "Saving…" : employee ? "Save changes" : "Add employee"}
                        </button>
                    </div>
                </form>
            </div>

            {/* Transfer Letter dialog — opens when admin changes Company on an existing employee */}
            {transferOpen && (
                <TransferDialog
                    employee={employee}
                    form={form}
                    transferData={transferData}
                    setTransferData={setTransferData}
                    transferring={transferring}
                    onCancel={() => setTransferOpen(false)}
                    onConfirm={confirmTransfer}
                />
            )}
        </div>
    );
}
